import Head from '@/components/Head/Head'
import Layout from '@/components/Layout/Layout'
import { ScrollProvider } from '@/context/Scroll'
import useCurrentPage from '@/hooks/use-current-page'
import useRemSizing from '@/hooks/use-rem-sizing'
import useStore from '@/store'
import '@/styles/global.scss'
import { deviceInfo } from '@/utils'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect, useRef } from 'react'

export default function App({ Component, pageProps }) {
  const hasSetGlobalData = useRef(false)
  const setGlobalData = useStore(state => state.setGlobalData)
  const setCurrentPageData = useStore(state => state.setCurrentPageData)
  const pageIsTransitioning = useStore(state => state.pageIsTransitioning)
  const enableInteraction = useStore(state => state.enableInteraction)
  const theme = useStore(state => state.theme)
  const router = useRouter()
  const setFontsLoaded = useStore(state => state.setFontsLoaded)
  const setPrevPath = useStore(state => state.setPrevPath)
  const pathsNavigated = useRef([])
  const { currentPath } = useCurrentPage()

  useEffect(() => {
    document.body.dataset.theme = theme
  }, [theme])

  useEffect(() => {
    if (!pathsNavigated.current.length) {
      pathsNavigated.current.push(currentPath)
      return
    }

    if (pathsNavigated.current[pathsNavigated.current.length - 1] === currentPath) {
      return
    }

    setPrevPath(pathsNavigated.current[pathsNavigated.current.length - 1])

    pathsNavigated.current.push(currentPath)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  useEffect(() => {
    document.fonts.ready.then(() => {
      setFontsLoaded(true)
    })
  }, [setFontsLoaded])

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID) return

    const handleRouteChange = url => {
      if (window.gtag) {
        window.gtag('config', process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID, {
          page_path: url,
        })
      }
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useRemSizing()

  useEffect(() => {
    let isEnabled = enableInteraction

    if (pageIsTransitioning) {
      isEnabled = false
    }

    document.body.dataset.enableInteraction = isEnabled
    if (isEnabled) {
      delete document.body.dataset.lenisPrevent
    } else {
      document.body.dataset.lenisPrevent = isEnabled
    }
  }, [pageIsTransitioning, enableInteraction])

  useEffect(() => {
    document.body.dataset.browser = deviceInfo.browser._parsedBrowserName.toLowerCase()
    document.body.dataset.os = deviceInfo.os._parsedOSName.toLowerCase()
  }, [])

  useEffect(() => {
    setCurrentPageData({
      title: pageProps?.title,
      slug: pageProps?.slug?.current,
    })
  }, [pageProps, setCurrentPageData])

  useEffect(() => {
    if (pageProps?.globalSettings?.navigation && !hasSetGlobalData.current) {
      hasSetGlobalData.current = true
      setGlobalData({
        ...pageProps.globalSettings,
      })
    }
  }, [pageProps, setGlobalData])

  return (
    <>
      <Head
        title={pageProps?.metadata?.title}
        description={pageProps?.metadata?.description}
        keywords={pageProps?.metadata?.keywords}
        shareImage={pageProps?.metadata?.image}
        favicon={pageProps?.metadata?.favicon}
        robots={pageProps?.metadata?.allowCrawlers}
      />
      {process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}`}
            strategy="afterInteractive"
          />
          <Script
            id="google-analytics"
            strategy="afterInteractive"
          >
            {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID}');
          `}
          </Script>
        </>
      )}
      <ScrollProvider>
        <Layout {...pageProps}>
          <Component {...pageProps} />
        </Layout>
      </ScrollProvider>
    </>
  )
}
